/**
 * pipeline.js — orchestrates the two-pass video-bg-remover workflow.
 *
 *   PASS 1  matte   decode frames in order → matte engine → VbrMatteStore
 *                   (alpha deflated + foreground JPEG + 240-px source thumb)
 *   ── optional manual rotoscoping happens here, in the editor ──
 *   PASS 2  export  matte → roto → edge → composite background → encoder
 *
 * TWO ENGINES, ONE CONTRACT. `engine:'ai'` runs RobustVideoMatting (recurrent,
 * so frames must arrive in presentation order and the state is reset per clip);
 * `engine:'chroma'` runs a colour/luma key, which is stateless and needs no
 * model. Both return the same RGBA canvas — RGB straight foreground, A matte —
 * which is why adding the second engine changed three lines here and nothing at
 * all in the store, the editor, the compositor or the encoder.
 *
 * Splitting the passes lets the editor sit between them: the matte is computed
 * once, the user refines it, then a fast compositing pass bakes the result.
 *
 * Memory: PASS 1 streams one frame at a time (decode → matte → store, then the
 * frame is dropped). PASS 2 streams matte → composite → encoder segment. The
 * FFmpeg FS is bounded by the encoder's chunking.
 *
 * Public API — window.VbrPipeline:
 *   prepare(opts)                 record the job parameters
 *   mattePass(cb)   → Promise     run RVM over the clip
 *   exportPass(cb)  → Promise<result>   composite + encode
 *   frameCount()    → number
 *
 *   opts: { file, url, fps, model, trimStart, trimEnd,
 *           processW, processH, videoW, videoH,
 *           bg:{mode,color,grad,image,blurRadius}, format, keepAudio,
 *           abortSignal }
 *   cb:   { onProgress(phase,pct,info), onFramePreview(canvas) }
 */
(function (root) {
    'use strict';

    var ctx = null;
    var ffmpegReady = false;

    function frameCount() {
        if (!ctx) return 0;
        return Math.max(1, Math.round((ctx.trimEnd - ctx.trimStart) * ctx.fps));
    }

    function prepare(opts) {
        ctx = Object.assign({}, opts);
        ffmpegReady = ffmpegReady; // keep across jobs
    }

    function checkAbort() {
        if (ctx && ctx.abortSignal && ctx.abortSignal.aborted) {
            throw new DOMException('aborted', 'AbortError');
        }
    }

    async function loadFFmpeg() {
        if (ffmpegReady && root.FFmpegProcessor && !root.FFmpegProcessor.__isStub
            && root.FFmpegProcessor.isReady()) return;
        // timbrica-core.js pre-registers a STUB FFmpegProcessor (marked
        // __isStub) that just queues calls — the real module must be loaded
        // explicitly, otherwise stub.init() rejects after a 15 s timeout.
        if (!root.FFmpegProcessor || root.FFmpegProcessor.__isStub) {
            await new Promise(function (resolve, reject) {
                var s = document.createElement('script');
                s.src = '/js/ffmpeg-processor.js';
                s.onload = resolve;
                s.onerror = function () { reject(new Error('ffmpeg load failed')); };
                document.head.appendChild(s);
            });
        }
        var proc = root.FFmpegProcessor;
        if (!proc || proc.__isStub) throw new Error('FFmpegProcessor unavailable');
        if (!proc.isReady()) await proc.init();
        ffmpegReady = true;
    }

    /* ───────────────────────── PASS 1 — matte ───────────────────────── */

    /* Which matte engine this job uses. Both honour the same contract — opaque
       frame in, RGBA canvas out with RGB = straight foreground and A = matte —
       so everything after this point (the rotoscoping editor, the compositor,
       the encoder) is engine-agnostic and neither had to change to gain the
       colour key. */
    function usingChroma() { return ctx && ctx.engine === 'chroma'; }

    async function mattePass(cb) {
        cb = cb || {};
        var total = frameCount();
        /* The frame → matte step (engine + the kept object) is the shared
           BgReplace stage: /remove-object-from-video runs the same one inside
           its render, and one implementation is what keeps the two alike. */
        var stage = BgReplace.createStage({
            engine: usingChroma() ? 'chroma' : 'ai', chroma: ctx.chroma,
            // «Сохранить предмет»: the tapped object joins the person, unioned
            // BEFORE the store, so review, the brush editor and the export all
            // start from the same matte (see keep.js).
            keep: root.VbrKeep || null
        });
        stage.begin();
        await stage.ready(ctx.abortSignal);   // abortable — Cancel works during model load
        checkAbort();

        VbrMatteStore.init({
            frameCount: total,
            width: ctx.processW, height: ctx.processH,
            fps: ctx.fps, trimStart: ctx.trimStart, trimEnd: ctx.trimEnd,
            fileName: ctx.fileName || 'video',
        });
        VbrRoto.configure(ctx.processW, ctx.processH);

        var t0 = performance.now();
        await VbrFrameSource.iterate({
            file: ctx.file, url: ctx.url, fps: ctx.fps,
            trimStart: ctx.trimStart, trimEnd: ctx.trimEnd,
            width: ctx.processW, height: ctx.processH,
            abortSignal: ctx.abortSignal,
        }, async function (frameCanvas, idx, tSec) {
            checkAbort();
            var t = (typeof tSec === 'number' && isFinite(tSec)) ? tSec : ctx.trimStart + idx / ctx.fps;
            var matte = await stage.matte(frameCanvas, t);
            // The source frame goes along for the review thumbnail — the "before"
            // half of the split the review phase shows; the store keeps a 240-px
            // JPEG of it (measured +3.9 ms/frame, 4.7 KB/frame).
            await VbrMatteStore.put(idx, matte, frameCanvas);
            if (cb.onFramePreview) cb.onFramePreview(matte);
            var done = idx + 1;
            var elapsed = (performance.now() - t0) / 1000;
            var eta = done > 1 ? (elapsed / done) * (total - done) : 0;
            if (cb.onProgress) cb.onProgress('matte', done / total, { frame: done, total: total, eta: eta });
        });
        checkAbort();
        var st = stage.stats();
        // the per-frame cost on THIS device and the lane that paid it — the
        // one number a phone decision can be taken on
        return { keptFrames: st.kept, total: total, msPerFrame: st.msPerFrame, provider: stage.provider() };
    }

    /* ───────────────────────── PASS 2 — export ───────────────────────── */

    /* Manual rotoscoping corrections, then the edge controls — in that order.
       Feather/choke are a presentation choice over whatever the matte says, so
       they must come last, and they live here rather than in the compositor so
       the SAME two lines serve the export and the review preview. */
    function refine(idx, matte) {
        return VbrMatteEdge.apply(VbrRoto.applyTo(idx, matte), ctx.edge);
    }

    /* One frame as the FILE will hold it: refine, then the chosen background.
       The export bakes every frame through this call and the review draws
       «Стало» through it too (02.10.2026: the review showed the bare cutout,
       and a person who picked «Размытие оригинала» asked where the blur was —
       it existed only after «Собрать файл»). `source` is the original frame a
       blur is made of: the export passes the decoded frame, the review its
       240-px thumbnail — upscaled under the blur, which hides the difference. */
    function frame(idx, matte, bg, source) {
        // edge controls are already in refine(): compose without them
        return BgReplace.compose(refine(idx, matte), bg, source || null, null);
    }


    async function exportPass(cb) {
        cb = cb || {};
        await loadFFmpeg();
        checkAbort();

        var proc = root.FFmpegProcessor;
        var total = VbrMatteStore.count();
        var session = await VbrEncoder.createSession(proc, {
            format: ctx.format,
            fps: ctx.fps,
            width: ctx.processW, height: ctx.processH,
            totalFrames: total,
            keepAudio: ctx.keepAudio,
            hasAudio: ctx.keepAudio,
            sourceFile: ctx.file,
            trimStart: ctx.trimStart, trimEnd: ctx.trimEnd,
        });

        try {
            if (ctx.bg && ctx.bg.mode === 'blur') {
                // Blur needs the original frame — re-decode in order, synced to mattes.
                await VbrFrameSource.iterate({
                    file: ctx.file, url: ctx.url, fps: ctx.fps,
                    trimStart: ctx.trimStart, trimEnd: ctx.trimEnd,
                    width: ctx.processW, height: ctx.processH,
                    abortSignal: ctx.abortSignal,
                }, async function (srcCanvas, idx) {
                    checkAbort();
                    var matte = await VbrMatteStore.get(idx);
                    if (!matte) return;
                    await session.addFrame(frame(idx, matte, ctx.bg, srcCanvas));
                    if (cb.onProgress) cb.onProgress('encode', (idx + 1) / total * 0.82, { frame: idx + 1, total: total });
                });
            } else {
                for (var i = 0; i < total; i++) {
                    checkAbort();
                    var matte = await VbrMatteStore.get(i);
                    if (!matte) continue;
                    await session.addFrame(frame(i, matte, ctx.bg, null));
                    if (cb.onProgress) cb.onProgress('encode', (i + 1) / total * 0.82, { frame: i + 1, total: total });
                }
            }
            await session.flush();
            var result = await session.finalize(function (p) {
                if (cb.onProgress) cb.onProgress('encode', 0.82 + p * 0.18, {});
            });
            return result;
        } catch (e) {
            try { await session.cleanup(); } catch (_) {}
            throw e;
        }
    }

    root.VbrPipeline = {
        prepare: prepare,
        mattePass: mattePass,
        exportPass: exportPass,
        frameCount: frameCount,
        /* The review preview must show exactly what the export will bake, so it
           renders through the same steps rather than a copy of them: refine()
           is the cutout alone, frame() the cutout on the chosen background. */
        refine: refine,
        frame: frame,
    };
})(typeof window !== 'undefined' ? window : globalThis);
